/**
 * Terrain Contact Form — Shared Logic
 * Handles flipbook intro, multi-step navigation, validation, and HubSpot submission.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /*  CDN images for the flipbook intro                                  */
  /* ------------------------------------------------------------------ */
  const DEFAULT_FLIPBOOK_IMAGES = [
    'https://cdn.shopify.com/s/files/1/0675/4225/3766/files/home-hero.jpg?v=1776087955',
    'https://cdn.shopify.com/s/files/1/0675/4225/3766/files/home-sayit.jpg?v=1776087955',
    'https://cdn.shopify.com/s/files/1/0675/4225/3766/files/home-meanit.jpg?v=1776087955',
    'https://cdn.shopify.com/s/files/1/0675/4225/3766/files/home-doit.jpg?v=1776087955',
    'https://cdn.shopify.com/s/files/1/0675/4225/3766/files/gallery-landscape-02.jpg?v=1776087996',
    'https://cdn.shopify.com/s/files/1/0675/4225/3766/files/gallery-landscape-03.jpg?v=1776087996',
    'https://cdn.shopify.com/s/files/1/0675/4225/3766/files/gallery-terrain-29181.jpg?v=1776088000',
    'https://cdn.shopify.com/s/files/1/0675/4225/3766/files/gallery-terrain-29255.jpg?v=1776087996',
    'https://cdn.shopify.com/s/files/1/0675/4225/3766/files/about-hero.jpg?v=1776087413',
    'https://cdn.shopify.com/s/files/1/0675/4225/3766/files/about-team-group.jpg?v=1776089048',
    'https://cdn.shopify.com/s/files/1/0675/4225/3766/files/home-approach-image.jpg?v=1776087955',
    'https://cdn.shopify.com/s/files/1/0675/4225/3766/files/about-values-image.jpg?v=1776087408',
  ];

  // Mutable — overridden at runtime by config from the gallery editor (Render app)
  let FLIPBOOK_IMAGES = DEFAULT_FLIPBOOK_IMAGES.slice();

  const EDITOR_API_BASE = 'https://terrain-gallery-editor.onrender.com';
  const CONFIG_URL = EDITOR_API_BASE + '/api/public/form-images';
  const SLACK_PROXY_URL = EDITOR_API_BASE + '/api/public/submit';

  const FLIPBOOK_INTERVAL_MS = 150;
  const FLIPBOOK_CYCLES = 3; // cycle through all images this many times
  const TOTAL_STEPS = 5; // steps 1-4 + success (step 5). Step 0 is the flipbook intro.

  let currentStep = 0;

  /* ------------------------------------------------------------------ */
  /*  Editor config (fetched from Render, fallback to localStorage)      */
  /* ------------------------------------------------------------------ */
  function applyBackgrounds(backgrounds) {
    if (!Array.isArray(backgrounds) || !backgrounds.length) return;
    document.querySelectorAll('.bg-layer .bg-photo').forEach((el, i) => {
      const src = backgrounds[i];
      if (src) el.style.backgroundImage = `url('${src}')`;
    });
  }

  function applyConfig(cfg) {
    if (!cfg) return;
    if (Array.isArray(cfg.flipbook) && cfg.flipbook.length) {
      FLIPBOOK_IMAGES = cfg.flipbook.slice();
    }
    applyBackgrounds(cfg.backgrounds);
  }

  async function loadEditorConfig() {
    // Apply any cached config immediately so layout doesn't flash
    try {
      const cached = JSON.parse(localStorage.getItem('terrain-form-images') || 'null');
      if (cached) applyConfig(cached);
    } catch {}

    // Then fetch fresh config from the editor app
    try {
      const res = await fetch(CONFIG_URL, { cache: 'no-store' });
      if (res.ok) {
        const cfg = await res.json();
        if (cfg) {
          applyConfig(cfg);
          try { localStorage.setItem('terrain-form-images', JSON.stringify(cfg)); } catch {}
        }
      }
    } catch (err) {
      console.warn('Could not fetch form config:', err);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Flipbook intro                                                     */
  /* ------------------------------------------------------------------ */
  function runFlipbook() {
    const container = document.getElementById('flipbook-intro');
    if (!container) {
      goToStep(1);
      return;
    }

    // Preload images
    const imgElements = FLIPBOOK_IMAGES.map((src) => {
      const img = new Image();
      img.src = src;
      return img;
    });

    // Create the flipbook image element
    let flipImg = container.querySelector('.flipbook-image');
    if (!flipImg) {
      flipImg = document.createElement('img');
      flipImg.className = 'flipbook-image';
      flipImg.alt = 'Terrain landscapes';
      // Insert at the beginning so the logo text overlays on top
      container.insertBefore(flipImg, container.firstChild);
    }

    let index = 0;
    const totalFrames = FLIPBOOK_IMAGES.length * FLIPBOOK_CYCLES;
    let frame = 0;

    flipImg.src = FLIPBOOK_IMAGES[0];

    const interval = setInterval(() => {
      frame++;
      index = frame % FLIPBOOK_IMAGES.length;
      flipImg.src = FLIPBOOK_IMAGES[index];

      if (frame >= totalFrames) {
        clearInterval(interval);
        // Fade out the flipbook, then reveal step 1
        container.classList.add('fade-out');
        setTimeout(() => {
          container.style.display = 'none';
          goToStep(1);
        }, 600);
      }
    }, FLIPBOOK_INTERVAL_MS);
  }

  /* ------------------------------------------------------------------ */
  /*  Step navigation                                                    */
  /* ------------------------------------------------------------------ */
  function getAllSteps() {
    return document.querySelectorAll('.form-step');
  }

  function getProgressDots() {
    return document.querySelectorAll('.step-dot, .progress-step');
  }

  function goToStep(n) {
    const steps = getAllSteps();
    if (n < 0 || n > TOTAL_STEPS) return;

    // Hide all steps
    steps.forEach((el) => el.classList.remove('active'));

    // Show requested step
    const target = document.querySelector(`.form-step[data-step="${n}"]`);
    if (target) {
      target.classList.add('active');
    }

    // Update progress dots
    const dots = getProgressDots();
    dots.forEach((dot) => {
      const dotStep = parseInt(dot.dataset.step, 10);
      dot.classList.remove('active', 'completed');
      if (dotStep === n) {
        dot.classList.add('active');
      } else if (dotStep < n) {
        dot.classList.add('completed');
      }
    });

    currentStep = n;

    // Scroll form into view smoothly
    const formContainer = document.querySelector('.terrain-form-container, .form-wrapper, form');
    if (formContainer && n > 0) {
      formContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Auto-focus the first text-like input on the new step so the user can
    // start typing immediately. Skip step 0 (splash) and step 5 (success).
    // We only focus text/email/tel/number/textarea inputs — never radios,
    // checkboxes, or selects, since focusing those can feel aggressive or
    // accidentally toggle them. A short delay lets the scrollIntoView settle
    // first so mobile doesn't fight the smooth scroll.
    if (target && n > 0 && n < 5) {
      setTimeout(() => {
        const firstInput = target.querySelector(
          'input[type="text"]:not([readonly]):not([disabled]),' +
          'input[type="email"]:not([readonly]):not([disabled]),' +
          'input[type="tel"]:not([readonly]):not([disabled]),' +
          'input[type="number"]:not([readonly]):not([disabled]),' +
          'textarea:not([readonly]):not([disabled])'
        );
        if (firstInput) {
          try { firstInput.focus({ preventScroll: true }); } catch (e) { firstInput.focus(); }
        }
      }, 320);
    }
  }

  function nextStep() {
    if (currentStep === 0) {
      goToStep(1);
      return;
    }
    if (!validateStep(currentStep)) return;

    if (currentStep === 4) {
      submitForm();
      return;
    }
    goToStep(currentStep + 1);
  }

  function prevStep() {
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Validation                                                         */
  /* ------------------------------------------------------------------ */
  function validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Normalize a user-entered phone string to E.164 for HubSpot.
   *   "(415) 555-0123"       → "+14155550123"
   *   "415-555-0123"         → "+14155550123"
   *   "4155550123"           → "+14155550123"
   *   "+1 415 555 0123"      → "+14155550123"
   *   "+44 20 7946 0958"     → "+442079460958"
   *   Returns "" on empty input, or the cleaned digits-only string
   *   (prefixed with +) if it doesn't look like a US number.
   */
  function formatPhoneForHubSpot(raw) {
    if (!raw) return '';
    const trimmed = String(raw).trim();
    if (!trimmed) return '';
    // Keep a leading + if present
    const hasPlus = trimmed.startsWith('+');
    const digits = trimmed.replace(/\D/g, '');
    if (!digits) return '';
    if (hasPlus) return '+' + digits;
    if (digits.length === 10) return '+1' + digits;           // 4155550123 → +14155550123
    if (digits.length === 11 && digits[0] === '1') return '+' + digits; // 14155550123 → +14155550123
    // Fallback: assume already-international digits, add +
    return '+' + digits;
  }

  // Live-format the phone input as the user types: "(415) 555-0123"
  function attachPhoneFormatter() {
    const input = document.querySelector('[name="phone"]');
    if (!input || input.dataset.phoneFormatterBound) return;
    input.dataset.phoneFormatterBound = '1';
    input.setAttribute('type', 'tel');
    input.setAttribute('inputmode', 'tel');
    input.setAttribute('autocomplete', 'tel');
    if (!input.placeholder) input.placeholder = '(555) 123-4567';

    input.addEventListener('input', (e) => {
      const raw = e.target.value;
      // Strip everything but digits, keep only up to 11 digits (1 + 10)
      let digits = raw.replace(/\D/g, '');
      // Drop leading country code 1 if present — we reformat as (xxx) xxx-xxxx
      if (digits.length > 10 && digits[0] === '1') digits = digits.slice(1);
      if (digits.length > 10) digits = digits.slice(0, 10);

      let formatted = '';
      if (digits.length === 0) formatted = '';
      else if (digits.length < 4) formatted = '(' + digits;
      else if (digits.length < 7) formatted = '(' + digits.slice(0, 3) + ') ' + digits.slice(3);
      else formatted = '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);

      // Only set if different to avoid cursor jumps on paste
      if (formatted !== raw) e.target.value = formatted;
    });
  }

  function clearErrors(stepEl) {
    stepEl.querySelectorAll('.field-error').forEach((el) => el.remove());
    stepEl.querySelectorAll('.input-error').forEach((el) => el.classList.remove('input-error'));
  }

  function showError(input, message) {
    input.classList.add('input-error');
    const err = document.createElement('span');
    err.className = 'field-error';
    err.textContent = message;
    // Insert after the input (or after its parent label-wrapper if one exists)
    const anchor = input.closest('.field-group') || input.parentElement;
    anchor.appendChild(err);
  }

  function validateStep(step) {
    const stepEl = document.querySelector(`.form-step[data-step="${step}"]`);
    if (!stepEl) return true;

    clearErrors(stepEl);
    let valid = true;

    if (step === 1) {
      const required = ['firstname', 'lastname', 'email'];
      required.forEach((name) => {
        const input = stepEl.querySelector(`[name="${name}"]`);
        if (!input) return;
        if (!input.value.trim()) {
          showError(input, 'This field is required');
          valid = false;
        }
      });
      // Email format
      const emailInput = stepEl.querySelector('[name="email"]');
      if (emailInput && emailInput.value.trim() && !validateEmail(emailInput.value.trim())) {
        showError(emailInput, 'Please enter a valid email address');
        valid = false;
      }
    }

    if (step === 2) {
      const required = ['address', 'city', 'zip_code', 'neighborhood_region'];
      required.forEach((name) => {
        const input = stepEl.querySelector(`[name="${name}"]`);
        if (!input) return;
        if (!input.value.trim()) {
          showError(input, 'This field is required');
          valid = false;
        }
      });
    }

    if (step === 3) {
      // At least one service checkbox
      const checks = stepEl.querySelectorAll('[name="what_can_we_help_you_with_"]:checked');
      if (checks.length === 0) {
        const checkboxGroup = stepEl.querySelector('.checkbox-group') || stepEl.querySelector('[name="what_can_we_help_you_with_"]').closest('.field-group');
        if (checkboxGroup) {
          const err = document.createElement('span');
          err.className = 'field-error';
          err.textContent = 'Please select at least one service';
          checkboxGroup.appendChild(err);
        }
        valid = false;
      }
      // How did you hear
      const howHeard = stepEl.querySelector('[name="how_did_you_hear_about_us_"]');
      if (howHeard && !howHeard.value) {
        showError(howHeard, 'Please select an option');
        valid = false;
      }
    }

    if (step === 4) {
      // SMS consent required
      // SMS consent is an optional opt-in under the phone field (Step 1); nothing to require here.
      // Communications consent checkbox
      const consent = stepEl.querySelector('[name="communications_consent"]');
      if (consent && !consent.checked) {
        showError(consent, 'You must agree to continue');
        valid = false;
      }
    }

    return valid;
  }

  /* ------------------------------------------------------------------ */
  /*  Collect form data                                                  */
  /* ------------------------------------------------------------------ */
  function collectFormData() {
    const val = (name) => {
      const el = document.querySelector(`[name="${name}"]`);
      return el ? el.value.trim() : '';
    };

    const services = [];
    document.querySelectorAll('[name="what_can_we_help_you_with_"]:checked').forEach((cb) => {
      services.push(cb.value);
    });

    const smsRadio = document.querySelector('[name="accepts_sms_marketing"]:checked');
    const smsBox = document.querySelector('[name="sms_consent_opt_in"]');

    return {
      firstname: val('firstname'),
      lastname: val('lastname'),
      email: val('email'),
      phone: val('phone'),
      address: val('address'),
      address_line_2: val('address_line_2'),
      city: val('city'),
      neighborhood_region: val('neighborhood_region'),
      zip_code: val('zip_code'),
      services,
      how_heard: val('how_did_you_hear_about_us_'),
      referral: val('did_someone_refer_you_to_us__let_us_know_their_name_below_'),
      sms_consent: smsBox ? ((smsBox.checked && val('phone')) ? 'Yes' : 'No') : (smsRadio ? smsRadio.value : ''),
      message: val('message'),
    };
  }

  /* ------------------------------------------------------------------ */
  /*  HubSpot submission                                                 */
  /* ------------------------------------------------------------------ */
  async function submitToHubSpot(formData) {
    const portalId = '21980058';
    const formGuid = '5d0d62ab-6e30-46f5-a270-b7cd65c00b24';

    const fields = [
      { name: 'firstname', value: formData.firstname },
      { name: 'lastname', value: formData.lastname },
      { name: 'email', value: formData.email },
      ...(formatPhoneForHubSpot(formData.phone) ? [{ name: 'phone', value: formatPhoneForHubSpot(formData.phone) }] : []),
      { name: 'address', value: formData.address },
      { name: 'address_line_2', value: formData.address_line_2 || '' },
      { name: 'city', value: formData.city },
      { name: 'zip_code', value: formData.zip_code },
      { name: 'neighborhood_region', value: formData.neighborhood_region },
      { name: 'what_can_we_help_you_with_', value: formData.services.join(';') },
      { name: 'how_did_you_hear_about_us_', value: formData.how_heard },
      { name: 'did_someone_refer_you_to_us__let_us_know_their_name_below_', value: formData.referral || '' },
      { name: 'text', value: formData.sms_consent === 'Yes' ? 'Yes' : 'No' } /* HubSpot's SMS-consent property is internally named "text" (Yes/No) */,
      // HubSpot's default "message" property has the internal name "text" and is required by this form
      
    ];

    const payload = {
      fields,
      context: {
        pageUri: window.location.href,
        pageName: 'Terrain Contact Form',
      },
      legalConsentOptions: {
        consent: {
          consentToProcess: true,
          text: 'By clicking submit below, you consent to allow Terrain to store and process the personal information submitted above to provide you the content requested, including marketing communications.',
        },
      },
    };

    const response = await fetch(
      `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formGuid}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) {
      let body = '';
      try { body = await response.text(); } catch {}
      console.error('HubSpot submission failed:', response.status, body);
    }
    return response.ok;
  }

  /* ------------------------------------------------------------------ */
  /*  Slack notification (via render proxy)                              */
  /* ------------------------------------------------------------------ */
  async function notifySlack(formData) {
    try {
      await fetch(SLACK_PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
    } catch (err) {
      console.warn('Slack notification failed (non-blocking):', err);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Submit wrapper                                                     */
  /* ------------------------------------------------------------------ */
  async function submitForm() {
    if (!validateStep(4)) return;

    const formData = collectFormData();

    // Show a loading state on the submit button
    const submitBtn = document.querySelector('.form-step[data-step="4"] .btn-next, .form-step[data-step="4"] .btn-submit');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.dataset.originalText = submitBtn.textContent;
      submitBtn.textContent = 'Submitting...';
    }

    try {
      const success = await submitToHubSpot(formData);
      if (success) {
        // Fire-and-forget Slack notification — don't block the success screen
        notifySlack(formData);
        goToStep(5);
        renderSuccessActions();
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = submitBtn.dataset.originalText || 'Submit';
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Success routing                                                    */
  /* ------------------------------------------------------------------ */
  function cameFromTerrain() {
    try {
      const ref = document.referrer || '';
      return ref.includes('terrainjh.com');
    } catch {
      return false;
    }
  }

  function renderSuccessActions() {
    const actionContainer = document.querySelector('.success-actions');
    if (!actionContainer) return;

    actionContainer.innerHTML = '';

    if (cameFromTerrain()) {
      const btn = document.createElement('button');
      btn.className = 'btn-primary btn-success-action';
      btn.textContent = 'Close & Return';
      btn.addEventListener('click', () => {
        window.close();
        // Fallback if window.close() is blocked
        setTimeout(() => {
          window.location.href = 'https://terrainjh.com';
        }, 300);
      });
      actionContainer.appendChild(btn);
    } else {
      const link = document.createElement('a');
      link.className = 'btn-primary btn-success-action';
      link.href = 'https://terrainjh.com';
      link.textContent = 'Visit Terrain';
      actionContainer.appendChild(link);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Initialization                                                     */
  /* ------------------------------------------------------------------ */
  function initForm() {
    // Load editor config (flipbook images + step backgrounds) from the Render app.
    // Fires async — any cached values apply immediately, fresh values override when they arrive.
    loadEditorConfig();

    // Auto-format the phone field as the user types
    attachPhoneFormatter();

    // Run flipbook if the intro container exists
    const flipbook = document.getElementById('flipbook-intro');
    if (flipbook) {
      currentStep = 0;
      runFlipbook();
    } else {
      goToStep(1);
    }

    // Delegate click events for navigation buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('.btn-next')) {
        e.preventDefault();
        nextStep();
      }
      if (e.target.closest('.btn-prev')) {
        e.preventDefault();
        prevStep();
      }
      if (e.target.closest('.btn-submit')) {
        e.preventDefault();
        submitForm();
      }
      // Progress dot clicks
      const dot = e.target.closest('.step-dot, .progress-step');
      if (dot) {
        const target = parseInt(dot.dataset.step, 10);
        if (target < currentStep) {
          goToStep(target);
        } else if (target > currentStep) {
          // Validate all steps up to the target
          let canAdvance = true;
          for (let i = currentStep; i < target; i++) {
            if (!validateStep(i)) {
              canAdvance = false;
              break;
            }
          }
          if (canAdvance) goToStep(target);
        }
      }
    });

    // Enter key advances to next step.
    // Handles three cases:
    //   - Step 0 (flipbook): skip intro
    //   - Steps 1-4 inside an input: advance
    //   - Focus on body/nothing: advance current step
    // Skips textareas (allow newlines), native <select> (let browser handle), and
    // the custom dropdown panel when it's open.
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      const t = e.target || document.body;
      const tag = (t.tagName || '').toLowerCase();
      if (tag === 'textarea') return;
      // Native select: let browser toggle the dropdown; skip unless it's our hidden proxy
      if (tag === 'select' && !t.closest('.custom-select')) return;
      // Allow Enter on Prev/Submit/Back buttons to click them normally
      if (tag === 'button' && !t.classList.contains('btn-next')) return;
      if (tag === 'a') return;
      // Custom dropdown open — let its own handler deal with Enter
      if (t.closest && t.closest('.custom-select.open')) return;

      // Find current step: prefer the one containing focus, else the active step
      let stepEl = t.closest && t.closest('.form-step');
      if (!stepEl) stepEl = document.querySelector('.form-step.active');
      if (!stepEl) return;

      e.preventDefault();

      // Flipbook intro: Enter skips it
      if (currentStep === 0) {
        const flipbook = document.getElementById('flipbook-intro');
        if (flipbook) {
          flipbook.classList.add('fade-out');
          setTimeout(() => { flipbook.style.display = 'none'; goToStep(1); }, 300);
        } else {
          goToStep(1);
        }
        return;
      }

      nextStep();
    });

    // Clear error styling on input
    document.addEventListener('input', (e) => {
      if (e.target.classList.contains('input-error')) {
        e.target.classList.remove('input-error');
        const group = e.target.closest('.field-group') || e.target.parentElement;
        const err = group.querySelector('.field-error');
        if (err) err.remove();
      }
    });

    // Skip flipbook on click/tap
    if (flipbook) {
      flipbook.addEventListener('click', () => {
        flipbook.classList.add('fade-out');
        setTimeout(() => {
          flipbook.style.display = 'none';
          goToStep(1);
        }, 300);
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Public API                                                         */
  /* ------------------------------------------------------------------ */
  window.TerrainForm = {
    initForm,
    nextStep,
    prevStep,
    goToStep,
    submitForm,
  };
})();
